import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { getChildPhotos, requestPhotoDownload, type ChildPhoto } from '../../services/parentService';
import { notifications } from '@mantine/notifications';

export const useChildPhotosInfinite = (
  childId: string | undefined,
  filters?: {
    date?: string;
    activityType?: string;
  }
) => {
  return useInfiniteQuery({
    queryKey: ['parent', 'photos', childId, filters],
    queryFn: ({ pageParam = 1 }) => 
      getChildPhotos(childId!, {
        page: pageParam,
        per_page: 20,
        ...filters,
      }),
    getNextPageParam: (lastPage) => {
      const pagination = lastPage.pagination;
      if (pagination && pagination.current_page < pagination.total_pages) {
        return pagination.current_page + 1;
      }
      return undefined;
    },
    enabled: !!childId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const usePhotoDownload = () => {
  // const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (photoIds: string[]) => requestPhotoDownload(photoIds),
    onSuccess: (data) => {
      notifications.show({
        title: 'Download Ready',
        message: 'Your photos are ready to download',
        color: 'green',
      });
      
      // Open download URL in new tab
      if (data.download_url) {
        window.open(data.download_url, '_blank');
      }
    },
    onError: (error: any) => {
      let message = 'Failed to prepare photos for download. Please try again.';
      
      // Handle specific error cases
      if (error.response?.status === 429) {
        message = 'You have reached the download limit. Please try again later.';
      } else if (error.response?.status === 404) {
        message = 'Some photos could not be found. They may have been deleted.';
      } else if (error.response?.status === 403) {
        message = 'You do not have permission to download these photos.';
      } else if (error.response?.data?.error?.message) {
        message = error.response.data.error.message;
      } else if (error.message) {
        message = error.message;
      }
      
      notifications.show({
        title: 'Download Failed',
        message,
        color: 'red',
      });
    },
  });
};

// Helper to group photos by date
export const useGroupedPhotos = (childId: string | undefined) => {
  const { data, ...rest } = useChildPhotosInfinite(childId);
  
  const groupedPhotos = data?.pages.reduce((acc, page) => {
    const photos = Array.isArray(page) ? page : (page?.items || []);
    photos.forEach((photo: ChildPhoto) => {
      const date = new Date(photo.timestamp).toLocaleDateString();
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(photo);
    });
    return acc;
  }, {} as Record<string, ChildPhoto[]>) || {};

  const allPhotos = data?.pages.flatMap(page => Array.isArray(page) ? page : (page?.items || [])) || [];

  return {
    ...rest,
    groupedPhotos,
    allPhotos,
    totalPhotos: data?.pages[0]?.pagination?.total_count || 0,
  };
};